import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { contentAPI } from '@/utils/api';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Loader } from '@/components/ui/loader';

export const ServiceDetail = () => {
  const { slug } = useParams();
  const { i18n, t } = useTranslation();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchService = useCallback(async () => {
    try {
      const res = await contentAPI.getServiceBySlug(slug);
      setService(res.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching service:', error);
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchService();
  }, [fetchService]);


  const getLangContent = (content) => {
    return content?.[i18n.language] || content?.en || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary">
        <Loader className="text-secondary" text={t('common.loading')} />
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-primary">
        <h2 className="text-white text-3xl mb-4">Service not found</h2>
        <Link to="/services">
          <Button variant="outline" className="border-secondary text-secondary hover:bg-secondary hover:text-primary">
            Back to Services
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-primary min-h-screen">
      <section data-testid="service-detail" className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <Link to="/services">
            <Button
              variant="ghost"
              className="text-gray-300 hover:text-secondary mb-8"
            >
              <ArrowLeft size={20} className="mr-2" />
              Back to Services
            </Button>
          </Link>

          <div className="mb-6">
            <span className="inline-block px-4 py-2 bg-secondary/10 text-secondary text-sm uppercase tracking-widest rounded-sm">
              {service.category === 'corporate_recovery' ? 'Corporate Recovery' : 'NPA Resolution'}
            </span>
          </div>

          <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6">
            {getLangContent(service.title)}
          </h1>

          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              {getLangContent(service.description)}
            </p>
          </div>

          <div className="mt-12 glass-card p-8 rounded-sm">
            <h3 className="font-heading text-2xl font-semibold text-secondary mb-4">
              Ready to get started?
            </h3>
            <p className="text-gray-300 mb-6">
              Contact us for a confidential consultation about this service.
            </p>
            <Link to="/contact">
              <Button className="bg-secondary text-primary hover:bg-secondary-light px-8 py-4 font-semibold uppercase tracking-wider rounded-sm btn-glow">
                {t('common.contact_us')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
